import { useEffect, useState, useMemo, useCallback } from "react";
import "./Trails.css";
import { Container } from "react-bootstrap";
import Pagination from 'react-bootstrap/Pagination';
import { RootState } from "../../../store";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from 'react-router-dom';
import AdminDataTable, {
  TableColumn,
  TableAction,
  createStatusBadge,
  createDifficultyBadge,
  formatDate,
  formatElevationGain,
  truncateText
} from "../../Reusables/AdminDataTable/AdminDataTable";
import FilterBar, { FilterField } from "../../Reusables/FilterBar/FilterBar";
import StatusChangeModal from "../../Reusables/StatusChangeModal/StatusChangeModal";
import { setTrailFilters, setCountryFilter } from "../../../store/slices/TrailFiltersSlice";
import { useTrails } from "../../../components/Trail/hooks/useTrails";
import { trailService } from "../../../components/Trail/services/trailService";
import { showSuccess, showError, showDeleteConfirm } from "../../../Utilities/sweetAlert";
import { Country } from "../../Data/Locations";
import { locationFilterService } from "../../../components/Trail/services/locationFilterService";

const Trails: React.FC = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const currentFilters = useSelector((state: RootState) => state.trailFilters);

    const {
        trails,
        totalTrails,
        currentPage,
        totalPages,
        loading,
        updateTrailStatus,
        setCurrentPage,
        fetchTrails
    } = useTrails({
        pageSize: 12,
        name: currentFilters.name,
        status: currentFilters.statusFilter, // Map statusFilter to status
        sortBy: currentFilters.sortBy,
        countryIds: currentFilters.countryFilter
    });

    const [showStatusModal, setShowStatusModal] = useState(false);
    const [selectedTrail, setSelectedTrail] = useState<any>(null);
    const [statusLoading, setStatusLoading] = useState(false);
    const [countries, setCountries] = useState<Country[]>([]);
    const [loadingLocations, setLoadingLocations] = useState(false);

    const openStatusModal = (trail: any) => {
        setSelectedTrail(trail);
        setShowStatusModal(true);
    };

    const handleStatusChange = async (newStatus: string) => {
        if (!selectedTrail) return;

        setStatusLoading(true);
        try {
            const success = await updateTrailStatus(selectedTrail.id, newStatus);
            if (success) {
                setShowStatusModal(false);
                setSelectedTrail(null);
            } else {
                showError('Update Failed', 'Failed to update trail status. Please try again.');
            }
        } catch (error) {
            console.error('Error updating trail status:', error);
            showError('Update Failed', 'Failed to update trail status. Please try again.');
        } finally {
            setStatusLoading(false);
        }
    };

    const closeStatusModal = () => {
        setShowStatusModal(false);
        setSelectedTrail(null);
    };

    const fetchLocations = async () => {
        setLoadingLocations(true);
        try {
            const countriesData = await locationFilterService.getCountries();
            
            // Ensure we always set arrays, even if the API returns unexpected data
            setCountries(Array.isArray(countriesData) ? countriesData : []);
        } catch (error: any) {
            console.error('Error fetching locations:', error);
            
            // Check if it's an authentication error
            if (error.response?.status === 401) {
                showError('Authentication Error', 'Please log in again to access this data');
            } else if (error.response?.status === 403) {
                showError('Access Denied', 'You do not have permission to access this data');
            } else {
                showError('Error', 'Failed to fetch location data');
            }
            
            // Set empty arrays on error to prevent crashes
            setCountries([]);
        } finally {
            setLoadingLocations(false);
        }
    };

    const handleCountryChange = useCallback(async (selectedCountries: any[]) => {
        // Extract the country IDs from the selected options
        const countryIds = selectedCountries.map(country => parseInt(country.value));
        dispatch(setCountryFilter(countryIds));
    }, [dispatch]);

    const handleDelete = async (trailId: number) => {
        const result = await showDeleteConfirm(
            'Permanently Delete Trail?',
            'This action will delete ALL trail data including photos, waypoints and reviews. This CANNOT be undone!',
            'Yes, delete it!',
            'Cancel'
        );

        if (!result.isConfirmed) return;

        setStatusLoading(true);
        try {
            await trailService.deleteTrail(trailId);
            showSuccess('Trail Deleted!', 'Trail and all related data deleted successfully!');

            fetchTrails({
                pageSize: 12,
                page: currentPage,
                name: currentFilters.name,
                status: currentFilters.statusFilter, // Map statusFilter to status
                sortBy: currentFilters.sortBy,
                countryIds: currentFilters.countryFilter
            });
        } catch (error) {
            console.error('Error deleting trail:', error);
            showError('Deletion Failed', 'Failed to delete trail. Please try again.');
        } finally {
            setStatusLoading(false);
        }
    };

    const handlePageChange = (newPage: number) => {
        const validNewPage = Math.min(Math.max(newPage, 1), totalPages);
        setCurrentPage(validNewPage);
    };

    useEffect(() => {
        fetchTrails({
            pageSize: 12,
            page: currentPage,
                            name: currentFilters.name,
                status: currentFilters.statusFilter, // Map statusFilter to status
                sortBy: currentFilters.sortBy,
                countryIds: currentFilters.countryFilter
        });
    }, [currentFilters, currentPage]); // Removed fetchTrails from deps

    useEffect(() => {
        fetchLocations();
    }, []);


    const filterFields: FilterField[] = useMemo(() => [
        {
            name: 'name',
            label: 'Trail Name',
            type: 'text',
            placeholder: 'Search by name...',
            defaultValue: currentFilters.name || ''
        },
        {
            name: 'countryFilter',
            label: 'Country',
            type: 'select',
            options: Array.isArray(countries) ? countries.map(country => ({ value: country.id.toString(), label: country.name })) : [],
            defaultValue: currentFilters.countryFilter || [],
            isMulti: true,
            onChange: (selected: any[]) => handleCountryChange(selected)
        },

        {
            name: 'statusFilter',
            label: 'Status',
            type: 'select',
            options: [
                { value: 'active', label: 'Active' },
                { value: 'pending', label: 'Pending' },
                { value: 'deleted', label: 'Inactive' }
            ],
            defaultValue: currentFilters.statusFilter || ''
        },
        {
            name: 'sortBy',
            label: 'Sort By',
            type: 'select',
            options: [
                { value: 'CreatedAtNewToOld', label: 'Newest First' },
                { value: 'CreatedAtOldToNew', label: 'Oldest First' }
            ],
            defaultValue: currentFilters.sortBy || ''
        }
    ], [countries, currentFilters, handleCountryChange]);

    const tableColumns: TableColumn[] = [
        {
            key: 'name',
            label: 'Name',
            sortable: true,
            width: '25%',
            render: (value, row) => (
                <div>
                    <strong>{truncateText(value, 30)}</strong>
                    <br />
                    <small className="text-muted">ID: {row.id}</small>
                </div>
            )
        },
        {
            key: 'status',
            label: 'Status',
            width: '12%',
            render: (value) => createStatusBadge(value)
        },
        {
            key: 'difficulty',
            label: 'Difficulty',
            width: '10%',
            render: (value) => createDifficultyBadge(value)
        },
        {
            key: 'distance',
            label: 'Distance',
            width: '8%',
            render: (value) => `${value} km`
        },
        {
            key: 'elevationGain',
            label: 'Elevation',
            width: '10%',
            render: (value) => formatElevationGain(value)
        },
        {
            key: 'rating',
            label: 'Rating',
            width: '8%',
            render: (value) => (
                <div className="d-flex align-items-center">
                    <span className="me-1">⭐</span>
                    <span>{value ? value : 'N/A'}</span>
                </div>
            )
        },
        {
            key: 'createdAt',
            label: 'Created',
            width: '10%',
            render: (value) => formatDate(value)
        },
        {
            key: 'photos',
            label: 'Photos',
            width: '6%',
            render: (value) => (
                <span className="badge bg-info">
                    {value ? value.length : 0}
                </span>
            )
        }
    ];

    const tableActions: TableAction[] = [
        {
            label: 'Edit',
            variant: 'primary',
            icon: 'fas fa-edit',
            onClick: (row) => navigate(`/trail/edit/${row.id}`)
        },
        {
            label: 'Change Status',
            variant: 'warning',
            icon: 'fas fa-toggle-off',
            onClick: (row) => openStatusModal(row)
        },
        {
            label: 'Delete',
            variant: 'danger',
            icon: 'fas fa-trash',
            onClick: (row) => handleDelete(row.id)
        }
    ];

    const handleFilterChange = (filters: Record<string, any>) => {
        dispatch(setTrailFilters({
            name: filters.name || '',
            statusFilter: filters.statusFilter || '',
            sortBy: filters.sortBy || '',
            countryFilter: Array.isArray(filters.countryFilter) ? filters.countryFilter : []
        }));
        setCurrentPage(1);
    };

    const handleAddNewTrail = () => {
        navigate('/trail/new');
    };

    return (
        <Container fluid className="admin-page-container py-4">
            {loadingLocations ? (
                <div className="text-center py-4">
                    <div className="spinner-border" role="status">
                        <span className="visually-hidden">Loading locations...</span>
                    </div>
                    <p className="mt-2">Loading location data...</p>
                </div>
            ) : (
                <FilterBar
                    title="Trail Filters"
                    fields={filterFields}
                    onFilter={handleFilterChange}
                    showAddButton={true}
                    addButtonLabel="Add New Trail"
                    onAddClick={handleAddNewTrail}
                    addButtonVariant="success"
                />
            )}

            <AdminDataTable
                data={trails}
                columns={tableColumns}
                actions={tableActions}
                loading={loading}
                emptyMessage="No trails found. Try adjusting your filters or add a new trail."
            />

            {totalPages > 1 && (
                <div className="d-flex justify-content-center mt-4">
                    <Pagination>
                        <Pagination.First
                            onClick={() => handlePageChange(1)}
                            disabled={currentPage === 1}
                        />
                        <Pagination.Prev
                            onClick={() => handlePageChange(currentPage - 1)}
                            disabled={currentPage === 1}
                        />

                        {[...Array(totalPages)].map((_, index) => (
                            <Pagination.Item
                                key={index + 1}
                                active={index + 1 === currentPage}
                                onClick={() => handlePageChange(index + 1)}
                            >
                                {index + 1}
                            </Pagination.Item>
                        ))}

                        <Pagination.Next
                            onClick={() => handlePageChange(currentPage + 1)}
                            disabled={currentPage === totalPages}
                        />
                        <Pagination.Last
                            onClick={() => handlePageChange(totalPages)}
                            disabled={currentPage === totalPages}
                        />
                    </Pagination>
                </div>
            )}

            {/* Total count at bottom */}
            <div className="d-flex justify-content-center mt-3">
                <div className="total-count-display">
                    <span className="text-muted">
                        Showing {trails.length} of {totalTrails} total trails
                    </span>
                </div>
            </div>

            <StatusChangeModal
                show={showStatusModal}
                onHide={closeStatusModal}
                onConfirm={handleStatusChange}
                currentStatus={selectedTrail?.status || ''}
                itemName={selectedTrail?.name || ''}
                itemType="trail"
                loading={statusLoading}
            />
        </Container>
    );
}

export default Trails;
